import {
  YES_NO,
  applicantSection,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * B1. Wniosek o wydanie zezwolenia na usunięcie drzewa lub krzewu — art. 83–83b ustawy o ochronie przyrody; formularz
 * udostępniony przez MKiŚ (art. 83b ust. 4), układ jak wzór Gminy Trzebnica: A. wnioskodawca, B. przedmiot (drzewa i
 * krzewy), C. miejsce, przyczyna i termin + pytania d–f, D. oświadczenia pod rygorem art. 233 KK, E. załączniki.
 * Zgłoszenie zamiaru usunięcia drzewa przez osobę fizyczną (art. 83f) to osobna procedura — kolejny obieg.
 */
const OWNER_CONSENT: EbiCondition = { field: "zgodaWlasciciela", in: ["zgoda", "informacja"] };

const B1: IEbiFormDefinition = {
  code: "B1",
  version: 1,
  title: "Wniosek o wydanie zezwolenia na usunięcie drzewa lub krzewu",
  category: EEbiFormCategory.Environment,
  office: EEbiFormOffice.Municipality,
  description: "Zezwolenie na wycinkę drzew lub krzewów (także na cele związane z działalnością gospodarczą).",
  keywords: ["drzewo", "krzew", "wycinka", "usunięcie", "zezwolenie", "ochrona przyrody"],
  organ: "Wójt / Burmistrz / Prezydent Miasta",
  legalBasis: "art. 83 ust. 1 i art. 83b ust. 1 ustawy z dnia 16 kwietnia 2004 r. o ochronie przyrody",
  request: "Wnoszę o wydanie zezwolenia na usunięcie drzew lub krzewów wskazanych w części B wniosku.",
  feeLabel: "wg stawek",
  fee: "Zezwolenie jest zwolnione z opłaty skarbowej; pełnomocnictwo — 17 zł. Opłata za usunięcie (art. 84) ustalana jest w decyzji.",
  deadlineLabel: "do 30 dni",
  deadline: "Do 30 dni, w sprawach szczególnie skomplikowanych do 60 dni (KPA).",
  sections: [
    applicantSection("A. Wnioskodawca"),
    representationSection(),
    {
      code: "przedmiot",
      title: "B. Informacje o przedmiocie wniosku",
      fields: [
        {
          code: "drzewa",
          type: "repeat",
          label: "Drzewa i krzewy do usunięcia",
          itemLabel: "Pozycja",
          required: true,
          min: 1,
          max: 50,
          fields: [
            {
              code: "rodzaj",
              type: "select",
              label: "Drzewo / krzew",
              required: true,
              width: "half",
              options: [
                { value: "drzewo", label: "drzewo" },
                { value: "krzew", label: "krzew" },
              ],
            },
            { code: "gatunek", type: "text", label: "Gatunek", required: true, width: "half", maxLength: 100, placeholder: "np. topola czarna" },
            { code: "ilosc", type: "number", label: "Ilość [szt.]", required: true, width: "half", min: 1, max: 9999 },
            {
              code: "wymiar",
              type: "text",
              label: "Obwód pnia na wys. 130 cm [cm] / powierzchnia krzewów [m²]",
              required: true,
              width: "half",
              maxLength: 50,
            },
          ],
        },
      ],
    },
    {
      code: "miejsce",
      title: "C. Miejsce, przyczyna i termin zamierzonego usunięcia",
      fields: [
        { code: "miejscowoscDzialki", type: "text", label: "Miejscowość", required: true, width: "half", maxLength: 100 },
        { code: "obreb", type: "text", label: "Obręb ewidencyjny", required: true, width: "half", maxLength: 100 },
        { code: "dzialka", type: "text", label: "Numer ewidencyjny działki", required: true, width: "half", maxLength: 100, placeholder: "np. 125/4" },
        { code: "termin", type: "text", label: "Termin zamierzonego usunięcia", required: true, width: "half", maxLength: 100, placeholder: "np. do końca lutego 2027 r." },
        { code: "przyczyna", type: "textarea", label: "Przyczyna zamierzonego usunięcia", required: true, maxLength: 3000 },
        {
          code: "dzialalnosc",
          type: "radio",
          label: "Czy usunięcie wynika z celu związanego z prowadzeniem działalności gospodarczej?",
          required: true,
          options: YES_NO,
        },
        {
          code: "dsu",
          type: "radio",
          label:
            "Czy planowane usunięcie wynika z realizacji przedsięwzięcia, dla którego wymagane jest uzyskanie decyzji o środowiskowych uwarunkowaniach albo postanowienia w sprawie uzgodnienia warunków realizacji przedsięwzięcia w zakresie oddziaływania na obszar Natura 2000?",
          required: true,
          options: YES_NO,
        },
        {
          code: "gatunkiChronione",
          type: "radio",
          label:
            "Czy w związku z planowaną wycinką wydane zostało zezwolenie w stosunku do gatunków chronionych na czynności podlegające zakazom określonym w art. 51 ust. 1 pkt 1–4 i 10 oraz w art. 52 ust. 1 pkt 1, 3, 7, 8, 12, 13 i 15 ustawy o ochronie przyrody?",
          required: true,
          options: YES_NO,
        },
        {
          code: "nasadzenia",
          type: "radio",
          label: "Czy planujesz nasadzenia zastępcze albo przesadzenie drzewa lub krzewu?",
          required: true,
          options: YES_NO,
        },
      ],
    },
    {
      code: "oswiadczenia",
      title: "D. Oświadczenia",
      fields: [
        {
          code: "podstawaWladania",
          type: "radio",
          label: "Oświadczam, że",
          required: true,
          options: [
            {
              value: "tytulPrawny",
              label:
                "drzewa lub krzewy znajdują się na nieruchomości wskazanej w części C, do której przysługuje mi tytuł prawny",
            },
            { value: "urzadzenia", label: "posiadam prawo własności urządzeń, o których mowa w art. 49 § 1 Kodeksu cywilnego" },
          ],
        },
        {
          code: "tytulPrawny",
          type: "text",
          label: "Tytuł prawny władania nieruchomością",
          required: true,
          visibleWhen: { field: "podstawaWladania", equals: "tytulPrawny" },
          maxLength: 200,
          placeholder: "np. własność, współwłasność, użytkowanie wieczyste, dzierżawa",
        },
        {
          code: "zgodaWlasciciela",
          type: "radio",
          label: "Zgoda właściciela nieruchomości",
          required: true,
          options: [
            { value: "wlasciciel", label: "jestem właścicielem nieruchomości" },
            { value: "zgoda", label: "posiadam zgodę właściciela nieruchomości" },
            {
              value: "informacja",
              label: "została przeze mnie udostępniona informacja, o której mowa w art. 83 ust. 4 ustawy o ochronie przyrody (spółdzielnia, wspólnota)",
            },
          ],
        },
        {
          code: "oswiadczenie233",
          type: "declaration",
          label: "Powyższe oświadczenia składam pod rygorem odpowiedzialności karnej z art. 233 Kodeksu karnego.",
          required: true,
        },
        feeInfo(
          "infoOplata",
          "Zezwolenie na usunięcie drzew lub krzewów jest zwolnione z opłaty skarbowej. Opłatę za usunięcie drzew (art. 84 ustawy o ochronie przyrody) — jeśli jest należna — ustala decyzja.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "rysunek",
      label: "Rysunek, mapa albo projekt zagospodarowania działki lub terenu z usytuowaniem drzewa lub krzewu",
      hint: "Usytuowanie względem granic nieruchomości i obiektów budowlanych.",
      required: true,
    },
    {
      code: "zgodaWlascicielaPlik",
      label: "Zgoda właściciela nieruchomości albo oświadczenie o udostępnieniu informacji (art. 83 ust. 4)",
      visibleWhen: OWNER_CONSENT,
      required: true,
    },
    {
      code: "planNasadzen",
      label: "Projekt planu nasadzeń zastępczych albo przesadzenia drzewa lub krzewu",
      hint: "Liczba, gatunek, miejsce i termin nasadzeń.",
      visibleWhen: { field: "nasadzenia", equals: "tak" },
      required: true,
    },
    {
      code: "decyzjaDsu",
      label: "Decyzja o środowiskowych uwarunkowaniach albo postanowienie uzgadniające (Natura 2000)",
      visibleWhen: { field: "dsu", equals: "tak" },
      required: true,
    },
    {
      code: "zezwolenieGatunki",
      label: "Zezwolenie w stosunku do gatunków chronionych na czynności podlegające zakazom",
      visibleWhen: { field: "gatunkiChronione", equals: "tak" },
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default B1;
